var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Divider, Stack } from "@mui/material";
import { DateTime } from "luxon";
import { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { ReservationAggregationService } from "../../../services/aggregation/reservationAggregation.service";
import { buildVisits, byNewestEnd, formatVisitDates, happened, hasEnded, isDuring } from "./visits";
import { GroupTitle, useDateColumnWidth, VisitRow } from "./VisitRow";
var FETCH_SIZE = 999;
// Sector history excludes cancelled/no-show bookings outside the selected stay.
var SectorPastVisits = function (_a) {
    var _b, _c, _d;
    var sector = _a.sector, profile = _a.profile, reservationId = _a.reservationId;
    var t = useTranslation().t;
    var _e = useState(), upcomingOpen = _e[0], setUpcomingOpen = _e[1];
    var _f = useState(true), stayOpen = _f[0], setStayOpen = _f[1];
    var _g = useState(), otherOpen = _g[0], setOtherOpen = _g[1];
    var visitsQuery = useQuery(['profilePreview', 'sectorVisits', profile.id, sector.id], function () { return ReservationAggregationService.listReservations({
        profileIds: [profile.id],
        sectorIds: [sector.id],
        limit: { offset: 0, limit: FETCH_SIZE, sort: [{ field: 'expectedEndDate', direction: 'DESC' }] }
    }); }, { enabled: !!profile.id && !!sector.id });
    var reservations = ((_c = (_b = visitsQuery.data) === null || _b === void 0 ? void 0 : _b.content) !== null && _c !== void 0 ? _c : []).filter(function (reservation) { return !!reservation.startDate; });
    var open = reservations.find(function (reservation) { return reservation.id === reservationId; });
    // "This stay" is the stay of the booking the dialog is open on (also a coming one), else the current stay.
    var stays = __spreadArray([profile.currentStay], ((_d = profile.upcomingStays) !== null && _d !== void 0 ? _d : []), true).filter(function (stay) { return !!stay; });
    var stay = (open && stays.find(function (candidate) { return isDuring(open, candidate); })) || profile.currentStay;
    var duringStay = stay ? reservations.filter(function (reservation) { return isDuring(reservation, stay); }) : [];
    var outside = reservations.filter(function (reservation) { return !duringStay.includes(reservation); });
    var upcomingVisits = buildVisits(outside.filter(function (reservation) { return !hasEnded(reservation); }), t, true).sort(byNewestEnd);
    var stayVisits = buildVisits(duringStay, t, true).sort(byNewestEnd);
    var otherVisits = buildVisits(outside.filter(happened), t, true).sort(byNewestEnd);
    var upcomingWidth = useDateColumnWidth(upcomingVisits);
    var stayWidth = useDateColumnWidth(stayVisits);
    var otherWidth = useDateColumnWidth(otherVisits);
    if (upcomingVisits.length === 0 && stayVisits.length === 0 && otherVisits.length === 0) {
        return null;
    }
    var hasStay = !!stay && stayVisits.length > 0;
    var isUpcomingOpen = upcomingOpen !== null && upcomingOpen !== void 0 ? upcomingOpen : !hasStay;
    var isOtherOpen = otherOpen !== null && otherOpen !== void 0 ? otherOpen : !hasStay;
    return (_jsxs(_Fragment, { children: [_jsx(Divider, {}), _jsxs(Stack, __assign({ gap: '12px' }, { children: [upcomingVisits.length > 0 && (_jsxs(Stack, __assign({ gap: '2px' }, { children: [_jsx(GroupTitle, __assign({ folded: !isUpcomingOpen, onToggle: function () { return setUpcomingOpen(!isUpcomingOpen); } }, { children: t('Upcoming') })), isUpcomingOpen && upcomingVisits.map(function (visit) { return _jsx(VisitRow, { visit: visit, dateWidth: upcomingWidth }, visit.id); })] }))), stay && hasStay && (_jsxs(Stack, __assign({ gap: '2px' }, { children: [_jsx(GroupTitle, __assign({ current: true, folded: !stayOpen, onToggle: function () { return setStayOpen(!stayOpen); } }, { children: "".concat(t('This stay'), " \u00B7 ").concat(formatVisitDates(DateTime.fromISO(stay.startDay), DateTime.fromISO(stay.endDay))) })), stayOpen && stayVisits.map(function (visit) { return _jsx(VisitRow, { visit: visit, dateWidth: stayWidth }, visit.id); })] }))), otherVisits.length > 0 && (_jsxs(Stack, __assign({ gap: '2px' }, { children: [_jsx(GroupTitle, __assign({ folded: !isOtherOpen, onToggle: function () { return setOtherOpen(!isOtherOpen); } }, { children: t('Other') })), isOtherOpen && otherVisits.map(function (visit) { return _jsx(VisitRow, { visit: visit, dateWidth: otherWidth }, visit.id); })] })))] }))] }));
};
export default SectorPastVisits;
